import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Users, Loader2, RefreshCw, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function StudentsMap() {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markersRef = useRef([]);
  const [data, setData] = useState({ students: [], total: 0, total_students: 0 });
  const [units, setUnits] = useState([]);
  const [unitFilter, setUnitFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [m, u] = await Promise.all([api.get("/students/geo"), api.get("/units")]);
      setData(m.data);
      setUnits(u.data);
    } catch (e) { toast.error("Erro ao carregar mapa"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filteredStudents = useMemo(() => {
    if (unitFilter === "all") return data.students;
    return data.students.filter(s => s.unit_id === unitFilter);
  }, [data.students, unitFilter]);

  useEffect(() => {
    if (!window.L || !containerRef.current) return;
    if (!mapRef.current) {
      mapRef.current = window.L.map(containerRef.current, { zoomControl: true, scrollWheelZoom: true })
        .setView([-23.5505, -46.6333], 12);
      window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap",
        maxZoom: 19,
      }).addTo(mapRef.current);
    }
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    if (filteredStudents.length === 0) return;

    const bounds = window.L.latLngBounds([]);
    filteredStudents.forEach(s => {
      const html = s.photo
        ? `<div class="fp-pin"><img src="${s.photo}" alt=""/></div>`
        : `<div class="fp-pin fp-pin-fallback">${s.name.charAt(0).toUpperCase()}</div>`;
      const icon = window.L.divIcon({ html, className: "fp-pin-wrap", iconSize: [44, 52], iconAnchor: [22, 52], popupAnchor: [0, -50] });
      const marker = window.L.marker([s.lat, s.lng], { icon }).addTo(mapRef.current);
      const popup = `
        <div style="min-width:200px">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
            ${s.photo ? `<img src="${s.photo}" style="width:36px;height:36px;border-radius:50%;object-fit:cover;border:2px solid #E11D48"/>` : `<div style="width:36px;height:36px;border-radius:50%;background:#E11D48;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:900">${s.name.charAt(0)}</div>`}
            <div><div style="font-weight:800;color:#0F172A">${s.name}</div><div style="font-size:11px;color:#64748B">${s.plan_name || "Sem plano"}</div></div>
          </div>
          <div style="font-size:12px;color:#475569;padding-top:4px;border-top:1px solid #F1F5F9">
            <div>📍 ${[s.neighborhood, s.city, s.state].filter(Boolean).join(", ") || "—"}</div>
            ${s.unit_name ? `<div>🏢 ${s.unit_name}</div>` : ""}
          </div>
        </div>`;
      marker.bindPopup(popup);
      markersRef.current.push(marker);
      bounds.extend([s.lat, s.lng]);
    });
    try { mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 }); } catch {}
  }, [filteredStudents]);

  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } }, []);

  return (
    <div className="space-y-4">
      <style>{`
        .fp-pin-wrap { background: transparent !important; border: none !important; }
        .fp-pin { width: 44px; height: 44px; border-radius: 50%; border: 3px solid #E11D48; background: #fff; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.25); position: relative; }
        .fp-pin img { width: 100%; height: 100%; object-fit: cover; }
        .fp-pin-fallback { display: flex; align-items: center; justify-content: center; color: #fff; background: #E11D48; font-weight: 900; font-size: 18px; }
        .fp-pin-wrap::after { content: ""; position: absolute; bottom: -10px; left: 50%; transform: translateX(-50%); width: 0; height: 0; border-left: 8px solid transparent; border-right: 8px solid transparent; border-top: 12px solid #E11D48; }
      `}</style>
      <PageHeader
        title="Mapa de Alunos"
        subtitle={`${filteredStudents.length} de ${data.total} localizado(s) · ${data.total_students} aluno(s) total`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-500"/>
              <Select value={unitFilter} onValueChange={setUnitFilter}>
                <SelectTrigger className="w-[220px]" data-testid="map-unit-filter"><SelectValue placeholder="Filtrar por unidade"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="map-unit-all">Todas as unidades</SelectItem>
                  {units.map(u => (
                    <SelectItem key={u.id} value={u.id} data-testid={`map-unit-${u.id}`}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={load} variant="outline" disabled={loading} data-testid="map-refresh"><RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}/>Atualizar</Button>
          </div>
        }
      />

      <div className="grid lg:grid-cols-4 gap-4">
        <Card className="lg:col-span-3 p-0 border-slate-200 overflow-hidden">
          {loading && data.students.length === 0 ? (
            <div className="h-[600px] flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="w-10 h-10 text-rose-500 animate-spin mx-auto mb-2"/>
                <p className="text-sm text-slate-500">Localizando alunos... (primeira carga pode demorar ~1s por CEP)</p>
              </div>
            </div>
          ) : (
            <div ref={containerRef} style={{ height: 600, width: "100%" }} data-testid="students-map-canvas"/>
          )}
        </Card>

        <Card className="p-5 border-slate-200 h-fit">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-rose-600"/>
            <h3 className="font-heading text-sm font-bold text-slate-500 uppercase tracking-wider">No mapa</h3>
          </div>
          <div className="space-y-2 max-h-[540px] overflow-y-auto" data-testid="map-students-list">
            {filteredStudents.map(s => (
              <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer" onClick={()=>{ if (mapRef.current) mapRef.current.setView([s.lat, s.lng], 15); }}>
                {s.photo
                  ? <img src={s.photo} className="w-8 h-8 rounded-full object-cover border-2 border-rose-300" alt=""/>
                  : <div className="w-8 h-8 rounded-full bg-rose-500 text-white flex items-center justify-center text-sm font-bold">{s.name[0]}</div>}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{s.name}</div>
                  <div className="text-xs text-slate-500 truncate">{s.unit_name || [s.city, s.state].filter(Boolean).join(", ") || "—"}</div>
                </div>
              </div>
            ))}
            {filteredStudents.length === 0 && !loading && (
              <div className="text-center py-6">
                <MapPin className="w-8 h-8 text-slate-300 mx-auto mb-2"/>
                <p className="text-xs text-slate-500">
                  {unitFilter === "all"
                    ? "Nenhum aluno com endereço cadastrado. Preencha o CEP no cadastro."
                    : "Nenhum aluno localizado nesta unidade."}
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
